import { MessageType } from '../../../types/chat.types';
import {
  removeWebsocketMessageByMessageId,
  removeWebsocketMessageByTempId,
  removeWebsocketMessagesByChat,
} from '../../../utils/chat.utils';
import { WsErrorsEnum } from '../constants/webSocketErrors.constants';
import {
  ChatSocketContextState,
  ChatSocketReducerActions,
  Message,
} from './chatSocketReducer.types';

export const chatSocketInitialState: ChatSocketContextState = {
  chatIdsToFetch: [],
  chatIdsToRemove: [],
  websocketMessages: [],
  receivedNotifications: [],
  errorRecoveringStateAfterDisconnect: {
    count: 0,
    hasErrorRecoveringState: false,
  },
  errorConnectingToWsChat: {
    hasError: false,
  },
};

export const chatSocketReducer = (
  state: ChatSocketContextState,
  action: ChatSocketReducerActions,
): ChatSocketContextState => {
  switch (action.type) {
    case 'RESET_STATE': {
      return chatSocketInitialState;
    }

    case 'CONNECTION_RECOVERED': {
      if (action.payload.recoveredSocketState) {
        return state;
      }

      return {
        ...state,
        errorRecoveringStateAfterDisconnect: {
          count: state.errorRecoveringStateAfterDisconnect.count + 1,
          hasErrorRecoveringState: true,
        },
      };
    }

    case 'WS_EXCEPTION': {
      const authErrors = [WsErrorsEnum.INVALID_AUTH_TOKEN, WsErrorsEnum.MISSING_AUTH_TOKEN];
      const hasErrorInConnectionToWs =
        !!action.payload.code && authErrors.includes(action.payload.code);

      return {
        ...state,
        wsException: action.payload,
        errorConnectingToWsChat: {
          hasError: hasErrorInConnectionToWs,
          notifyUser: false,
        },
      };
    }

    case 'ADD_MESSAGE_WITHOUT_CONFIRMATION': {
      const { submitMessage, user, chat } = action.payload;

      const message: Message = {
        chat,
        error: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        body: submitMessage.body,
        data: submitMessage.data,
        type: MessageType.MESSAGE,
        chatId: submitMessage.chatId,
        tempId: submitMessage.tempId,
        commercialName: user.brand?.name || user.store?.name || '',
        user: {
          id: user.userId,
          email: user.email,
          name: user.name,
          profileUrl: user.profileUrl,
          surname: user.surname,
        },
      };

      const newWebsocketMessages = removeWebsocketMessageByTempId(
        state.websocketMessages,
        submitMessage.tempId,
      );

      return {
        ...state,
        websocketMessages: [...newWebsocketMessages, message],
      };
    }

    case 'ADD_MANY_MESSAGES_WITHOUT_CONFIRMATION': {
      const { submitMessages, user } = action.payload;

      const messages = submitMessages.map<Message>((submitMessage) => ({
        user,
        error: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        body: submitMessage.body,
        data: submitMessage.data,
        type: MessageType.MESSAGE,
        chatId: submitMessage.chatId,
        tempId: submitMessage.tempId,
        commercialName: user.brand?.name || user.store?.name || '',
      }));

      const newWebsocketMessages = submitMessages.reduce(
        (acc, submitMessage) => removeWebsocketMessageByTempId(acc, submitMessage.tempId),
        state.websocketMessages,
      );

      return {
        ...state,
        websocketMessages: [...newWebsocketMessages, ...messages],
      };
    }

    case 'ADD_NOTIFICATION': {
      const audio = new Audio('/sounds/notification.mp3');
      audio.play();

      return {
        ...state,
        receivedNotifications: [...state.receivedNotifications, action.payload.lastNotification],
      };
    }

    case 'REMOVE_NOTIFICATIONS_BY_MESSAGE_IDS': {
      const { messageIds } = action.payload;
      const receivedNotifications = state.receivedNotifications.filter(
        (notification) => !messageIds.includes(notification.messageId),
      );
      return {
        ...state,
        receivedNotifications,
      };
    }

    case 'UPDATE_MESSAGE_WITH_ERROR': {
      const { tempId } = action.payload;
      const message = state.websocketMessages.find((wsMessage) => wsMessage.tempId === tempId);

      if (!message) {
        return state;
      }
      const newWebsocketMessages = removeWebsocketMessageByTempId(state.websocketMessages, tempId);
      return {
        ...state,
        websocketMessages: [...newWebsocketMessages, { ...message, error: true }],
      };
    }

    case 'MESSAGE_RECEIVED': {
      const { message } = action.payload;

      if (!message.tempId) {
        return {
          ...state,
          websocketMessages: [...state.websocketMessages, message],
        };
      }

      const newWebsocketMessages = removeWebsocketMessageByTempId(
        state.websocketMessages,
        message.tempId,
      );

      return {
        ...state,
        websocketMessages: [...newWebsocketMessages, message],
      };
    }

    case 'CLEAN_WEBSOCKET_MESSAGE_BY_TEMP_ID': {
      const { tempId }: { tempId: string } = action.payload;
      const websocketMessages = removeWebsocketMessageByTempId(state.websocketMessages, tempId);
      return {
        ...state,
        websocketMessages,
      };
    }

    case 'CLEAN_WEBSOCKET_MESSAGE_BY_MESSAGE_ID': {
      const { messageId }: { messageId: string } = action.payload;
      const websocketMessages = removeWebsocketMessageByMessageId(
        state.websocketMessages,
        messageId,
      );
      return {
        ...state,
        websocketMessages,
      };
    }

    case 'CLEAN_MESSAGES_BY_CHAT_ID': {
      const { chatId }: { chatId: string } = action.payload;
      const websocketMessages = removeWebsocketMessagesByChat(state.websocketMessages, chatId);
      return {
        ...state,
        websocketMessages,
      };
    }

    case 'CHAT_CREATED': {
      return {
        ...state,
        chatIdsToFetch: [...state.chatIdsToFetch, action.payload.chatId],
      };
    }

    case 'REMOVED_FROM_CHAT': {
      return {
        ...state,
        chatIdsToRemove: [...state.chatIdsToRemove, action.payload.chatId],
      };
    }

    case 'REMOVE_CHAT_TO_FETCH': {
      return {
        ...state,
        chatIdsToFetch: state.chatIdsToFetch.filter((chatId) => chatId !== action.payload.chatId),
      };
    }

    case 'REMOVE_CHAT_TO_REMOVE': {
      return {
        ...state,
        chatIdsToRemove: state.chatIdsToRemove.filter((chatId) => chatId !== action.payload.chatId),
      };
    }

    default:
      throw new Error('Ação do reducer não encontrada');
  }
};
